// WebexComplianceSettings.jsx
import React, { useState, useEffect } from "react";
import { Shield, Users, AlertCircle, CheckCircle, Loader } from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const WebexComplianceSettings = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [complianceOfficers, setComplianceOfficers] = useState([]);
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchComplianceOfficers();
    fetchComplianceStatus();
  }, []);

  const fetchComplianceOfficers = async () => {
    try {
      const response = await apiService.getCompanyUsers({
        role: "admin",
      });
      setComplianceOfficers(response.users || []);
    } catch (error) {
      console.error("Error fetching compliance officers:", error);
    }
  };

  const fetchComplianceStatus = async () => {
    try {
      const response = await apiService.getWebexComplianceStatus();
      setComplianceStatus(response.data);
      setSelectedOfficer(response.data?.complianceOfficerId);
    } catch (error) {
      console.error("Error fetching compliance status:", error);
    }
  };

  const handleConnect = async () => {
    if (!selectedOfficer) {
      setMessage({
        type: "error",
        text: "Please select a compliance officer first",
      });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const response = await apiService.connectWebexCompliance(selectedOfficer);

      window.location.href = response.authUrl;
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Failed to initiate compliance connection",
      });
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Disconnect compliance recording access? This will stop automatic recording retrieval.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await apiService.disconnectWebexCompliance();
      setMessage({
        type: "success",
        text: "Compliance integration disconnected",
      });
      fetchComplianceStatus();
    } catch (error) {
      setMessage({
        type: "error",
        text: "Failed to disconnect compliance integration",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`rounded-lg border p-6 transition-colors ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center text-white">
          <Shield size={20} />
        </div>
        <div>
          <h2
            className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Webex Compliance Integration
          </h2>
          <p
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Organization-wide recording access for compliance and quality
            assurance
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div
        className={`p-4 rounded-lg mb-6 ${isDarkMode ? "bg-blue-500/10 border border-blue-500/30" : "bg-blue-50 border border-blue-200"}`}
      >
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-blue-400 mt-0.5 shrink-0" />
          <div
            className={`text-sm ${isDarkMode ? "text-blue-400" : "text-blue-700"}`}
          >
            <strong className="font-medium">How Compliance Access Works</strong>
            <ol
              className={`mt-2 space-y-1 list-decimal list-inside ${isDarkMode ? "text-blue-400/80" : "text-blue-600/80"}`}
            >
              <li>
                Select a compliance officer who will authorize the integration
              </li>
              <li>
                They'll sign in to Webex and grant organization-wide recording
                access
              </li>
              <li>
                The CRM can then retrieve <em>all</em> call recordings
                automatically
              </li>
              <li>
                Recordings are attached to lead/account records for compliance
                audits
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* Feedback Message */}
      {message && (
        <div
          className={`flex items-center gap-3 p-4 rounded-lg mb-6 ${
            message.type === "success"
              ? isDarkMode
                ? "bg-green-500/10 border border-green-500/30 text-green-400"
                : "bg-green-50 border border-green-200 text-green-600"
              : isDarkMode
                ? "bg-red-500/10 border border-red-500/30 text-red-400"
                : "bg-red-50 border border-red-200 text-red-600"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Connected State */}
      {complianceStatus?.configured ? (
        <div className="space-y-4">
          <div
            className={`p-4 rounded-lg ${isDarkMode ? "bg-green-500/10 border border-green-500/30" : "bg-green-50 border border-green-200"}`}
          >
            <div className="flex items-center gap-2 text-green-400 mb-3">
              <CheckCircle size={18} />
              <span className="font-medium">Compliance Access Active</span>
            </div>
            <div
              className={`text-sm space-y-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              <div>
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  Authorized by:{" "}
                </span>
                <span className="font-medium">
                  {complianceStatus.complianceOfficerName}
                </span>
              </div>
              <div>
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  Organization ID:{" "}
                </span>
                <span className="font-mono text-xs">
                  {complianceStatus.webexOrgId}
                </span>
              </div>
              <div>
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  Recordings webhook:{" "}
                </span>
                <span className="font-mono text-xs">
                  {complianceStatus.webhookUrl}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={handleDisconnect}
            disabled={loading}
            className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-white"
          >
            {loading ? (
              <>
                <Loader size={18} className="animate-spin" />
                Disconnecting...
              </>
            ) : (
              "Disconnect Compliance Access"
            )}
          </button>
        </div>
      ) : (
        /* Not Connected State */
        <div className="space-y-4">
          {/* Compliance Officer Selection */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              Select Compliance Officer
            </label>
            <select
              value={selectedOfficer || ""}
              onChange={(e) => setSelectedOfficer(e.target.value)}
              className={`w-full px-4 py-3 rounded-lg focus:ring-1 focus:ring-purple-500 transition-colors ${isDarkMode ? "bg-[#1A1D24] border border-gray-700 text-white focus:border-purple-500" : "bg-white border border-gray-300 text-gray-900 focus:border-purple-400"}`}
              disabled={loading}
            >
              <option value="">Choose a user...</option>
              {complianceOfficers.map((officer) => (
                <option key={officer.id} value={officer.id}>
                  {officer.name} ({officer.email})
                </option>
              ))}
            </select>
            <p
              className={`text-xs mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              This user will authorize organization-wide recording access
            </p>
          </div>

          <button
            onClick={handleConnect}
            disabled={loading || !selectedOfficer}
            className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-white"
          >
            {loading ? (
              <>
                <Loader size={18} className="animate-spin" />
                Redirecting to Webex...
              </>
            ) : (
              <>
                <Shield size={18} />
                Connect Compliance Access
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default WebexComplianceSettings;
